import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { Link } from '@/components/Link/Link';
import Button from '@/design_system/Button';
import InputText from '@/design_system/InputText';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import { useCreateSession } from '@/models/user';

import { useCompleteLogin } from '../useCompleteLogin';

export const PasswordStep = ({
  email,
  onPrevious,
  onMfaRequired,
}: {
  email: string;
  onPrevious: () => void;
  onMfaRequired: (mfaToken: string) => void;
}) => {
  const { t } = useLingui();

  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const {
    mutateAsync: createSession,
    isPending: isCreateSessionPending,
    isSuccess: isCreateSessionSuccess,
  } = useCreateSession();

  const completeLogin = useCompleteLogin();

  function onSubmit() {
    createSession({
      email,
      password,
    })
      .then((response) => {
        if ('mfaToken' in response) {
          // For now there is only one MFA method (email) so the code has already been sent to the user
          // In the future, when there are other methods, we'll allow the user to choose one
          onMfaRequired(response.mfaToken);
        } else {
          completeLogin(response);
        }
      })
      .catch((err) => {
        if (err.status === 403 && document.cookie.includes('__session_exists')) {
          // The user probably logged in in another tab, let's reload the page to get redirected
          window.location.reload();
        } else if (err.message === 'Invalid credentials') {
          setError(
            t({
              id: 'login.error.incorrect-email-or-password',
              message: 'Email or password incorrect',
            })
          );
        } else {
          console.error(err);
          setError(
            (err.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
          );
        }
      });
  }

  return (
    <form
      onSubmit={(evt) => {
        evt.preventDefault();
        onSubmit();
      }}
    >
      <Title size="200" weight="bold" as="h1" align="center">
        <Trans id="login.title">Log in to continue</Trans>
      </Title>
      <Stack gap="1.5rem">
        <InputText
          isRequired
          name="email"
          label={<Trans id="login.input.email.label">Email</Trans>}
          type="email"
          inputMode="email"
          value={email}
          isDisabled
        />
        <InputText
          isRequired
          name="password"
          label={<Trans id="login.input.password.label">Password</Trans>}
          type="password"
          placeholder={t({
            id: 'login.input.password.placeholder',
            message: 'Enter your password...',
          })}
          messageType={error ? 'error' : undefined}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          value={password}
          onChange={setPassword}
        />
        {error && (
          <Paragraph size="100" weight="medium" color="danger" align="center">
            {error}
          </Paragraph>
        )}
        <Link className="text-link" to={`/forgotten-password?email=${encodeURIComponent(email)}`}>
          <Trans id="login.forget-password">Forgot your password?</Trans>
        </Link>
        <Stack row gap="0.5rem">
          <Button type="button" variant="secondary" onPress={onPrevious} style={{ flex: 1 }}>
            <Trans id="login.back">Back</Trans>
          </Button>
          <Button
            type="submit"
            isLoading={isCreateSessionPending || isCreateSessionSuccess}
            style={{ flex: 1 }}
          >
            <Trans id="login.submit">Log in</Trans>
          </Button>
        </Stack>
      </Stack>
    </form>
  );
};
